import { FormlyFieldConfig } from '@ngx-formly/core';

export interface FormlyValidationError {
  label: string;
  messages: string[];
}

/**
 * Recursively traverses a formly field tree and collects validation errors
 * from invalid leaf fields. Skips structural types (section, block, formly-group).
 */
export function collectFormlyValidationErrors(
  field: FormlyFieldConfig
): FormlyValidationError[] {
  const errors: FormlyValidationError[] = [];

  const traverse = (f: FormlyFieldConfig): void => {
    if (
      f.type === 'section' ||
      f.type === 'block' ||
      f.type === 'formly-group'
    ) {
      f.fieldGroup?.forEach(traverse);
      return;
    }

    if (f.formControl?.invalid) {
      const label = f.props?.label || f.key?.toString() || '(Unnamed field)';
      const controlErrors = f.formControl.errors ?? {};

      const messages: string[] = Object.keys(controlErrors).map(errKey => {
        const validationMessages = f.validation?.messages ?? {};
        if (validationMessages[errKey]) {
          const msgDef = validationMessages[errKey];
          if (typeof msgDef === 'function') {
            const result = msgDef(controlErrors[errKey], f);
            return typeof result === 'object'
              ? JSON.stringify(result)
              : String(result);
          }
          return String(msgDef);
        }
        return `${label} is invalid (${errKey})`;
      });

      errors.push({ label, messages });
    }

    f.fieldGroup?.forEach(traverse);
  };

  traverse(field);
  return errors;
}

/**
 * Collects validation errors from a formly layout array and returns
 * a pipe-delimited summary string suitable for analytics tracking.
 */
export function getFormlyValidationErrorsSummary(
  layout: FormlyFieldConfig[]
): string {
  const allErrors: FormlyValidationError[] = [];

  layout.forEach(field => {
    allErrors.push(...collectFormlyValidationErrors(field));
  });

  if (allErrors.length === 0) {
    return 'Form is invalid';
  }

  return allErrors
    .map(err => `${err.label}: ${err.messages.join(', ')}`)
    .join(' | ');
}
